import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  anotarAula,
  concluirAula,
  lerAula,
  lerPainel,
  pedirCertificado,
  registrarAberturaDaAula,
  type Aula,
  type AulaNoPainel,
  type Certificado,
  type Leitura,
  type ModuloNoPainel,
} from "../lib/cursoNoHub";
import Carregando from "../componentes/Carregando";
import { Cabine, Segmentos } from "../componentes/FerramentaUi";
import { TileDeFato } from "../componentes/CalendarioUi";
import { Tique } from "../componentes/CursoBonusUi";
import { IconeExterno } from "../componentes/ui";
import CertificadoDoCurso from "../componentes/CertificadoDoCurso";
import { BlocoRenderizado } from "./FerramentaDeConteudo";

// O curso que MORA no hub (000375). O primeiro é o Fundamentos da Reforma Tributária, o low ticket
// que vinha da Lovable: as aulas são vídeos do ScaleUp com o texto ao lado, o módulo seguinte abre
// com o anterior concluído, e o certificado sai com todas as aulas concluídas.
//
// Três telas, pelo segmento depois do slug — sub-rota, e não estado, para o aluno voltar pelo
// navegador e mandar o link de uma aula a alguém (a decisão do HUB DP):
//   /ferramenta/<card>                              o painel: módulos, progresso, a próxima aula
//   /ferramenta/<card>/aula/<módulo>/<aula>         a aula: o vídeo, o texto, concluir, anotar
//   /ferramenta/<card>/certificado                  o certificado, para imprimir
//
// Esta tela NÃO decide nada: se a aula está aberta, quantas contam e se o certificado sai é o banco
// (`curso_situacao`). A tela mostra a resposta e a frase que veio com ela.

export default function CursoNoHub({ card }: { card: string }) {
  const { "*": resto = "" } = useParams();
  const partes = resto.split("/").filter(Boolean);
  const base = `/ferramenta/${card}`;

  if (partes[0] === "aula" && partes[1] && partes[2]) {
    return <TelaDaAula card={card} base={base} secao={partes[1]} pagina={partes[2]} />;
  }
  if (partes[0] === "certificado") return <TelaDoCertificado card={card} base={base} />;
  if (partes.length > 0) {
    return (
      <Moldura>
        <Recado titulo="Esta página do curso não existe" base={base}>
          O endereço não corresponde a nenhuma aula. Volte ao curso e escolha a aula na lista.
        </Recado>
      </Moldura>
    );
  }
  return <TelaDoPainel card={card} base={base} />;
}

/** O `?curso=` viaja junto: é ele que faz o "Voltar" da barra de cima saber de qual curso o aluno veio. */
function useComBusca() {
  const { search } = useLocation();
  return (caminho: string) => `${caminho}${search}`;
}

/** Lê uma vez por chave, e lê de novo quando `recarregar` é chamado — sem desmontar a tela. */
function useLeitura<T>(ler: () => Promise<Leitura<T>>, chave: string) {
  const [estado, setEstado] = useState<Leitura<T> | null>(null);
  const [versao, setVersao] = useState(0);
  const lerRef = useRef(ler);
  lerRef.current = ler;
  useEffect(() => {
    let vivo = true;
    lerRef.current().then((r) => {
      if (vivo) setEstado(r);
    });
    return () => {
      vivo = false;
    };
  }, [chave, versao]);
  const recarregar = useCallback(() => setVersao((v) => v + 1), []);
  // A chave nova começa sem resposta: sem isto, a aula anterior apareceria por um instante com o
  // endereço da seguinte.
  const [chaveDoEstado, setChaveDoEstado] = useState(chave);
  if (chaveDoEstado !== chave) {
    setChaveDoEstado(chave);
    setEstado(null);
  }
  return { estado, recarregar };
}

function Moldura({ children }: { children: ReactNode }) {
  return <main className="mx-auto w-full max-w-6xl px-4 pt-6 pb-12 sm:px-6">{children}</main>;
}

function Recado({ titulo, base, children }: { titulo: string; base: string; children: ReactNode }) {
  const comBusca = useComBusca();
  return (
    <div className="surface mx-auto max-w-xl p-7 text-center">
      <h1 className="text-xl font-bold text-titulo">{titulo}</h1>
      <p className="mt-2 text-sm leading-relaxed text-cinza">{children}</p>
      <Link to={comBusca(base)} className="pill-cta mt-5 inline-flex">
        Ver as aulas
      </Link>
    </div>
  );
}

function SemLeitura({ fase, base }: { fase: "sem-acesso" | "erro"; base: string }) {
  return fase === "sem-acesso" ? (
    <Recado titulo="Você não tem este curso" base={base}>
      Ele não está entre os cursos que você tem hoje. Se você acabou de comprar, ele aparece assim que a
      compra for processada.
    </Recado>
  ) : (
    <Recado titulo="Não conseguimos carregar o curso" base={base}>
      O problema é nosso, não seu. Tente de novo em alguns minutos.
    </Recado>
  );
}

// ---------------------------------------------------------------------------
// O painel
// ---------------------------------------------------------------------------

function TelaDoPainel({ card, base }: { card: string; base: string }) {
  const comBusca = useComBusca();
  const { estado } = useLeitura(() => lerPainel(card), card);

  if (!estado) return <Carregando texto="Carregando as aulas…" />;
  if (estado.fase !== "pronto") {
    return (
      <Moldura>
        <SemLeitura fase={estado.fase} base={base} />
      </Moldura>
    );
  }
  const p = estado.dados;
  const concluido = p.total > 0 && p.concluidas === p.total;
  // A posição de cada aula que CONTA, na ordem do curso, para a faixa acender as concluídas no lugar
  // delas (o aluno vê QUAIS faltam, e não "três").
  const queContam = p.modulos.flatMap((m) => m.aulas).filter((a) => a.papel === "aula");
  const porcentagem = p.total > 0 ? Math.round((p.concluidas / p.total) * 100) : 0;

  const acao = p.proxima ? (
    <div className="flex flex-wrap items-center gap-3">
      <Link
        to={comBusca(`${base}/aula/${p.proxima.secao}/${p.proxima.pagina}`)}
        data-continuar
        className="pill-cta"
      >
        {p.concluidas === 0 ? "Começar o curso" : "Continuar"}
      </Link>
      {/* Sobre navy: `text-white` e `text-navy-mute`, nunca `tinta`/`cinza`. */}
      <span className="min-w-0 text-sm text-navy-mute">
        <span className="font-semibold text-white">{p.proxima.nome}</span> · {p.proxima.secaoNome}
      </span>
    </div>
  ) : concluido ? (
    <Link to={comBusca(`${base}/certificado`)} data-ver-certificado className="pill-cta">
      Ver o certificado
    </Link>
  ) : undefined;

  // A descrição não repete os números da faixa e dos tiles: diz o que eles não dizem.
  const descricao = concluido
    ? "Você concluiu o curso. As aulas continuam abertas para rever quando quiser, e o certificado está pronto."
    : "As aulas são em vídeo, com o texto da aula logo abaixo. Cada módulo abre quando você conclui o anterior, e o certificado sai com todas as aulas concluídas.";

  let numero = 0;

  return (
    <Moldura>
      <Cabine
        rotulo="Curso"
        titulo={p.cursoNome}
        descricao={descricao}
        acao={acao}
        fatos={
          p.total > 0 ? (
            <>
              <TileDeFato
                rotulo="Concluídas"
                valor={`${p.concluidas} de ${p.total}`}
                apoio={concluido ? "curso concluído" : p.concluidas === 0 ? "nenhuma ainda" : `${porcentagem}% do curso`}
              />
              <TileDeFato
                rotulo="Certificado"
                valor={p.certificado.emitidoEm ? "emitido" : p.certificado.liberado ? "disponível" : "falta concluir"}
                apoio={
                  p.certificado.emitidoEm
                    ? "pronto para imprimir"
                    : p.certificado.liberado
                      ? "com todas as aulas concluídas"
                      : faltamEmTexto(p.total - p.concluidas)
                }
              />
            </>
          ) : undefined
        }
        rodape={
          p.total > 0 ? (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-faixa-progresso>
              <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.09em] text-navy-mute">
                Seu progresso
              </span>
              <div className="min-w-48 flex-1">
                <Segmentos
                  total={p.total}
                  acesos={p.concluidas}
                  mapa={queContam.map((a) => a.concluida)}
                  rotulo="Aulas concluídas"
                />
              </div>
              <span data-progresso className="font-display text-sm font-bold tabular-nums text-white">
                {p.concluidas} de {p.total} · {porcentagem}%
              </span>
            </div>
          ) : undefined
        }
      />

      {p.modulos.length === 0 ? (
        // Lista vazia sem frase é lida como tela quebrada.
        <p className="surface mt-6 p-6 text-sm text-cinza">
          As aulas deste curso ainda não foram publicadas. O seu acesso está liberado — se demorar, o
          suporte resolve.
        </p>
      ) : (
        <div className="mt-6 space-y-5">
          {p.modulos.map((m, i) => {
            const contam = m.papel === null || m.papel === "aula";
            const indice = p.modulos.slice(0, i + 1).filter((x) => x.papel === null || x.papel === "aula").length;
            return (
              <CartaoDoModulo key={m.slug} modulo={m} indice={contam ? indice : null}>
                {m.aulas.map((a) => {
                  if (a.papel === "aula") numero += 1;
                  return (
                    <LinhaDaAula
                      key={a.slug}
                      aula={a}
                      numero={a.papel === "aula" ? numero : null}
                      para={comBusca(`${base}/aula/${m.slug}/${a.slug}`)}
                      ehProxima={p.proxima?.secao === m.slug && p.proxima?.pagina === a.slug}
                    />
                  );
                })}
                {m.papel === "encerramento" && (
                  <li className="px-4 py-3.5 sm:px-5" data-linha-certificado>
                    {p.certificado.liberado ? (
                      <Link to={comBusca(`${base}/certificado`)} className="font-semibold text-link hover:underline">
                        {p.certificado.emitidoEm ? "Ver o certificado" : "Emitir o certificado"} →
                      </Link>
                    ) : (
                      <span className="text-sm text-cinza">
                        O certificado sai quando você concluir as {p.total} aulas — {faltamEmTexto(p.total - p.concluidas)}.
                      </span>
                    )}
                  </li>
                )}
              </CartaoDoModulo>
            );
          })}
        </div>
      )}
    </Moldura>
  );
}

function faltamEmTexto(n: number): string {
  return n === 1 ? "falta 1 aula" : `faltam ${n} aulas`;
}

function Cadeado({ className = "size-3.5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

/**
 * Um módulo: o card com o cabeçalho e as aulas dentro. `<section role="group" aria-labelledby>` com
 * `<h2>`, nunca `<fieldset><legend>` (o legend sai flutuando sobre a borda do card arredondado).
 */
function CartaoDoModulo({
  modulo,
  indice,
  children,
}: {
  modulo: ModuloNoPainel;
  indice: number | null;
  children: ReactNode;
}) {
  const id = `modulo-${modulo.slug}`;
  const queContam = modulo.aulas.filter((a) => a.papel === "aula");
  const feitas = queContam.filter((a) => a.concluida).length;
  const rotulo = modulo.papel === "bonus" ? "Bônus" : modulo.papel === "encerramento" ? "Encerramento" : indice ? `Módulo ${indice}` : null;
  return (
    <section
      role="group"
      aria-labelledby={id}
      data-modulo={modulo.slug}
      data-liberado={modulo.liberado ? "sim" : "nao"}
      className="surface overflow-hidden"
    >
      <header className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-borda px-4 py-3 sm:px-5">
        <div className="min-w-0 flex-1">
          {/* O rótulo só quando o nome não o diz: "Módulo 1 — Apresentação" com "MÓDULO 1" em cima é o
              mesmo fato duas vezes. */}
          {rotulo && !modulo.nome.toLowerCase().startsWith(rotulo.toLowerCase()) && (
            <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.09em] text-cinza">{rotulo}</p>
          )}
          <h2 id={id} className="font-display text-lg leading-tight font-bold text-titulo">
            {modulo.nome}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          {!modulo.liberado ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-chip px-2 py-0.5 text-[11px] font-semibold text-chip-tinta">
              <Cadeado className="size-3" />
              {modulo.papel === "encerramento" ? "abre com todas as aulas" : "abre com o módulo anterior"}
            </span>
          ) : queContam.length > 0 && feitas === queContam.length ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-aviso-fundo px-2 py-0.5 text-[11px] font-semibold text-aviso-tinta">
              <Tique className="size-3" />
              concluído
            </span>
          ) : null}
          {queContam.length > 0 && (
            <>
              <div className="w-20 sm:w-28">
                <Segmentos
                  total={queContam.length}
                  acesos={feitas}
                  mapa={queContam.map((a) => a.concluida)}
                  rotulo={`Aulas concluídas em ${modulo.nome}`}
                  sobreNavy={false}
                />
              </div>
              <span data-modulo-progresso className="text-xs font-semibold tabular-nums text-cinza">
                {feitas} de {queContam.length}
              </span>
            </>
          )}
        </div>
      </header>
      <ol className="divide-y divide-borda">{children}</ol>
    </section>
  );
}

/**
 * Uma aula na lista. A aberta é um LINK para a tela dela; a travada não é link — o clique não
 * levaria a nada além do cadeado, e a linha já diz por quê. O tique aqui é só ESTADO: concluir é
 * gesto da tela da aula, depois de assistir.
 */
function LinhaDaAula({
  aula,
  numero,
  para,
  ehProxima,
}: {
  aula: AulaNoPainel;
  numero: number | null;
  para: string;
  ehProxima: boolean;
}) {
  const fatos: string[] = [];
  if (aula.duracao) fatos.push(aula.duracao);
  if (aula.papel === "bonus") fatos.push(aula.linkExterno ? "abre em outra ferramenta" : "bônus");
  if (aula.papel === "encerramento") fatos.push("não conta para o certificado");
  if (aula.temAnotacao) fatos.push("com anotação sua");

  const marcador = (
    <span
      aria-hidden="true"
      data-numeral
      className={`grid size-9 shrink-0 place-items-center rounded-[11px] font-display text-sm font-bold ${
        aula.concluida
          ? "bg-aviso-fundo text-aviso-tinta"
          : !aula.liberada
            ? "bg-superficie-2 text-cinza"
            : "bg-tile text-alerta-tinta"
      }`}
    >
      {aula.concluida ? <Tique className="size-4" /> : !aula.liberada ? <Cadeado className="size-4" /> : numero ?? "★"}
    </span>
  );

  const corpo = (
    <div className="flex items-center gap-3 sm:gap-4">
      {marcador}
      <div className="min-w-0 flex-1">
        <p className={`leading-snug font-medium ${aula.liberada ? "text-tinta" : "text-cinza"}`}>
          <span className="sr-only">
            {numero ? `Aula ${numero}: ` : ""}
            {aula.concluida ? "concluída — " : !aula.liberada ? "travada — " : ""}
          </span>
          {aula.nome}
          {ehProxima && (
            <span
              data-selo-proxima
              className="ml-2 inline-flex items-center rounded-full bg-alerta-fundo px-2 py-0.5 align-middle text-[11px] font-semibold text-alerta-tinta"
            >
              próxima
            </span>
          )}
        </p>
        {fatos.length > 0 && <p className="mt-0.5 text-xs text-cinza">{fatos.join(" · ")}</p>}
      </div>
      {aula.liberada && (
        <span aria-hidden="true" className="shrink-0 text-sm font-semibold text-alerta-tinta">
          {aula.temVideo ? (aula.concluida ? "Rever" : "Assistir") : "Abrir"} →
        </span>
      )}
    </div>
  );

  return (
    <li
      data-aula={aula.slug}
      data-concluida={aula.concluida ? "sim" : "nao"}
      data-liberada={aula.liberada ? "sim" : "nao"}
      data-proxima={ehProxima ? "sim" : undefined}
      className={ehProxima ? "bg-tile" : ""}
    >
      {aula.liberada ? (
        <Link to={para} className="block px-4 py-3.5 transition hover:bg-superficie-2 sm:px-5">
          {corpo}
        </Link>
      ) : (
        <div className="px-4 py-3.5 sm:px-5">{corpo}</div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// A aula
// ---------------------------------------------------------------------------

function TelaDaAula({ card, base, secao, pagina }: { card: string; base: string; secao: string; pagina: string }) {
  const comBusca = useComBusca();
  const chave = `${secao}/${pagina}`;
  const { estado, recarregar } = useLeitura(() => lerAula(card, secao, pagina), chave);
  const [gravando, setGravando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const aberta = estado?.fase === "pronto" && estado.dados.existe && estado.dados.liberada;
  // Abrir a aula fica registrado (o "onde parei"), uma vez por aula, e só quando ela de fato abriu.
  useEffect(() => {
    if (aberta) registrarAberturaDaAula(card, secao, pagina);
  }, [aberta, card, secao, pagina]);
  useEffect(() => setErro(null), [chave]);

  if (!estado) return <Carregando texto="Carregando a aula…" />;
  if (estado.fase !== "pronto") {
    return (
      <Moldura>
        <SemLeitura fase={estado.fase} base={base} />
      </Moldura>
    );
  }
  const a: Aula = estado.dados;
  if (!a.existe) {
    return (
      <Moldura>
        <Recado titulo="Esta aula não existe" base={base}>
          O endereço não corresponde a nenhuma aula do curso. Escolha a aula na lista.
        </Recado>
      </Moldura>
    );
  }
  if (!a.liberada) {
    return (
      <Moldura>
        <div data-aula-travada className="surface mx-auto max-w-xl p-7 text-center">
          <p className="inline-flex items-center gap-1.5 text-[0.6875rem] font-semibold uppercase tracking-[0.09em] text-cinza">
            <Cadeado className="size-3" /> {a.secaoNome}
          </p>
          <h1 className="mt-1 text-xl font-bold text-titulo">{a.nome}</h1>
          <p className="mt-2 text-sm leading-relaxed text-cinza">{a.mensagem}</p>
          <Link to={comBusca(base)} className="pill-cta mt-5 inline-flex">
            Ver as aulas
          </Link>
        </div>
      </Moldura>
    );
  }

  const seConclui = a.papel === "aula";

  async function alternar(concluida: boolean) {
    setGravando(true);
    setErro(null);
    const r = await concluirAula(card, secao, pagina, concluida);
    setGravando(false);
    if (!r.ok) {
      setErro(r.mensagem);
      return;
    }
    // Concluir pode abrir o módulo seguinte: a próxima aula é relida do banco, que é quem sabe.
    recarregar();
  }

  return (
    <Moldura>
      <nav aria-label="Trilha" className="text-xs tracking-[0.14em] text-cinza uppercase">
        <Link to={comBusca(base)} className="underline decoration-borda hover:text-titulo">
          Todas as aulas
        </Link>
        <span aria-hidden="true" className="mx-1.5">
          ›
        </span>
        <span>{a.secaoNome}</span>
      </nav>
      <h1 className="mt-2 font-display text-2xl font-bold text-titulo sm:text-3xl" data-aula-nome>
        {a.nome}
      </h1>
      {a.duracao && <p className="mt-1 text-sm text-cinza">{a.duracao} de vídeo</p>}

      {a.scaleupId && (
        // O vídeo é do ScaleUp, como na Lovable. `aspect-video` + `w-full` porque player de largura
        // fixa estoura no celular, que é onde o aluno assiste. Sem `autoplay`: abrir a aula não é
        // pedir para tocar.
        <div className="mt-5 aspect-video w-full overflow-hidden rounded-xl bg-navy-ink" data-video>
          <iframe
            key={a.scaleupId}
            src={`https://player.scaleup.com.br/embed/${encodeURIComponent(a.scaleupId)}`}
            title={`Vídeo da aula: ${a.nome}`}
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="size-full border-0"
          />
        </div>
      )}

      <div className="surface mt-5 flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5" data-barra-da-aula>
        {seConclui ? (
          <button
            type="button"
            role="checkbox"
            aria-checked={a.concluida}
            disabled={gravando}
            onClick={() => alternar(!a.concluida)}
            data-concluir
            className={`inline-flex items-center gap-2 rounded-full border-2 px-4 py-2 text-sm font-bold transition disabled:opacity-60 ${
              a.concluida
                ? "border-aviso-borda bg-aviso-fundo text-aviso-tinta"
                : "border-borda bg-superficie text-titulo hover:border-laranja"
            }`}
          >
            <Tique className="size-4" />
            {a.concluida ? "Aula concluída" : "Marcar como concluída"}
          </button>
        ) : a.papel === "bonus" ? (
          <p className="text-sm text-cinza">Conteúdo bônus: não conta para o certificado.</p>
        ) : (
          <p className="text-sm text-cinza">O encerramento do curso não conta para o certificado.</p>
        )}
        {a.linkExterno && (
          <a href={a.linkExterno} target="_blank" rel="noreferrer" data-link-externo className="pill-cta">
            Abrir
            <IconeExterno className="size-4" />
            <span className="sr-only">(abre em outra aba)</span>
          </a>
        )}
        {erro && (
          <p role="alert" className="w-full text-sm text-erro-tinta">
            {erro}
          </p>
        )}
      </div>

      {a.blocos.length > 0 && (
        <article className="surface mt-5 space-y-4 p-5 sm:p-7" data-texto-da-aula>
          <h2 className="text-[0.6875rem] font-semibold uppercase tracking-[0.09em] text-cinza">Sobre esta aula</h2>
          {a.blocos.map((b, i) => (
            <BlocoRenderizado key={i} bloco={b} id={`bloco-${i}`} />
          ))}
        </article>
      )}

      <AnotacaoDaAula
        key={chave}
        inicial={a.anotacao}
        salvar={async (t) => {
          const r = await anotarAula(card, secao, pagina, t);
          return r.ok ? null : r.mensagem;
        }}
      />

      <nav aria-label="Aulas vizinhas" className="mt-6 grid gap-3 sm:grid-cols-2">
        <Vizinha rumo="anterior" alvo={a.anterior} base={base} />
        <Vizinha rumo="proxima" alvo={a.proxima} base={base} />
      </nav>
    </Moldura>
  );
}

function Vizinha({
  rumo,
  alvo,
  base,
}: {
  rumo: "anterior" | "proxima";
  alvo: { secao: string; pagina: string; nome: string; liberada: boolean } | null;
  base: string;
}) {
  const comBusca = useComBusca();
  if (!alvo) return <span />;
  const rotulo = rumo === "anterior" ? "← Anterior" : "Próxima →";
  const alinhamento = rumo === "proxima" ? "sm:text-right" : "";
  if (!alvo.liberada) {
    return (
      <div data-vizinha={rumo} data-liberada="nao" className={`surface p-4 ${alinhamento}`}>
        <p className="text-xs font-semibold text-cinza">{rotulo}</p>
        <p className="mt-0.5 font-medium text-cinza">{alvo.nome}</p>
        <p className="mt-0.5 text-xs text-cinza">Abre quando você concluir as aulas deste módulo.</p>
      </div>
    );
  }
  return (
    <Link
      to={comBusca(`${base}/aula/${alvo.secao}/${alvo.pagina}`)}
      data-vizinha={rumo}
      data-liberada="sim"
      className={`surface porta block p-4 ${alinhamento}`}
    >
      <p className="text-xs font-semibold text-alerta-tinta">{rotulo}</p>
      <p className="mt-0.5 font-medium text-tinta">{alvo.nome}</p>
    </Link>
  );
}

/**
 * A anotação do aluno sobre a aula, com autosave (900 ms depois da última tecla), como a dos cursos
 * bônus. O texto é do componente depois de montado: devolver o valor aparado para o campo moveria o
 * cursor de quem digita.
 */
function AnotacaoDaAula({ inicial, salvar }: { inicial: string | null; salvar: (t: string) => Promise<string | null> }) {
  const [texto, setTexto] = useState(inicial ?? "");
  const [situacao, setSituacao] = useState<"parado" | "salvando" | "salvo" | "erro">("parado");
  const [erro, setErro] = useState<string | null>(null);
  const primeiro = useRef(true);
  const salvarRef = useRef(salvar);
  salvarRef.current = salvar;

  useEffect(() => {
    if (primeiro.current) {
      primeiro.current = false;
      return;
    }
    setSituacao("salvando");
    const t = setTimeout(async () => {
      const e = await salvarRef.current(texto);
      setErro(e);
      setSituacao(e ? "erro" : "salvo");
    }, 900);
    return () => clearTimeout(t);
  }, [texto]);

  return (
    <section className="surface mt-5 p-5 sm:p-6" aria-labelledby="anotacao-titulo" data-anotacao>
      <h2 id="anotacao-titulo" className="font-display text-base font-bold text-titulo">
        Sua anotação
      </h2>
      <label htmlFor="anotacao-campo" className="sr-only">
        Anotação desta aula
      </label>
      <textarea
        id="anotacao-campo"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        rows={4}
        maxLength={5000}
        placeholder="O que você quer lembrar desta aula?"
        className="mt-2 w-full resize-y rounded-xl border border-borda bg-fundo p-3 text-sm leading-relaxed text-tinta placeholder:text-cinza focus-visible:ring-3 focus-visible:ring-laranja/40 focus-visible:outline-none"
      />
      <p className="mt-1 flex flex-wrap items-center justify-between gap-2 text-xs text-cinza">
        <span>Só você vê esta anotação.</span>
        <span role="status" data-estado-anotacao={situacao} className={situacao === "erro" ? "text-erro-tinta" : ""}>
          {situacao === "salvando" ? "salvando…" : situacao === "salvo" ? "salvo" : situacao === "erro" ? erro : ""}
        </span>
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// O certificado
// ---------------------------------------------------------------------------

function TelaDoCertificado({ card, base }: { card: string; base: string }) {
  const comBusca = useComBusca();
  const [cert, setCert] = useState<Certificado | null>(null);

  useEffect(() => {
    let vivo = true;
    pedirCertificado(card).then((r) => {
      if (vivo) setCert(r);
    });
    return () => {
      vivo = false;
    };
  }, [card]);

  if (!cert) return <Carregando texto="Preparando o certificado…" />;
  if (!cert.ok) {
    return (
      <Moldura>
        <Recado titulo="O certificado ainda não está disponível" base={base}>
          {cert.mensagem}
        </Recado>
      </Moldura>
    );
  }
  return (
    <Moldura>
      <div className="no-imprimir flex flex-wrap items-end justify-between gap-4">
        <div>
          {/* A mesma trilha da aula: hierarquia INTERNA do curso. O "Voltar" da barra de cima leva à página
              do curso, que é outro lugar. */}
          <nav aria-label="Trilha" className="text-xs tracking-[0.14em] text-cinza uppercase">
            <Link to={comBusca(base)} className="underline decoration-borda hover:text-titulo">
              Todas as aulas
            </Link>
            <span aria-hidden="true" className="mx-1.5">
              ›
            </span>
            <span>Certificado</span>
          </nav>
          <h1 className="mt-2 font-display text-2xl font-bold text-titulo">Seu certificado</h1>
          <p className="mt-1 max-w-xl text-sm leading-relaxed text-cinza">
            Com o nome do seu cadastro e a data de hoje. Para guardar em PDF,
            clique em imprimir e escolha "Salvar como PDF" na janela que abrir.
          </p>
        </div>
        <button type="button" onClick={() => window.print()} className="pill-cta" data-imprimir>
          Imprimir ou salvar em PDF
        </button>
      </div>
      <div className="mt-6 flex justify-center">
        <CertificadoDoCurso nome={cert.nome} curso={cert.cursoNome} data={cert.data} />
      </div>
    </Moldura>
  );
}
